import { CloseIcon, InfoIcon, WarningIcon } from "components/SvgIcons";
import { Box, Text, Spacer } from "@chakra-ui/react";
import { useLocale } from "hooks";
import React from "react";
import type {
  NotificationLevelEnum,
  UserNotificationType
} from "@shared/graphql.types";
import { colours } from "@shared/ui.design-system";
import { space } from "theme/themeConfig";
import { createMarkup } from "utils/createMarkup";
import { Wrapper } from "./NotificationsBanner.styles";
import { getNotificationData } from "./NotificationsBanner.utils";

type NotificationStyles = {
  borderColor: string;
  backgroundColor: string;
  Icon: JSX.Element;
};

const notificationConfig: Record<NotificationLevelEnum, NotificationStyles> = {
  error: {
    borderColor: colours.error.medium,
    backgroundColor: colours.error.light,
    Icon: (
      <WarningIcon
        boxSize="2rem"
        color={colours.error.medium}
        mx={1}
        data-testid="warning-icon"
      />
    )
  },
  warn: {
    borderColor: colours.warning.dark,
    backgroundColor: colours.warning.medium,
    Icon: (
      <InfoIcon
        boxSize="2rem"
        color={colours.warning.dark}
        mx={1}
        data-testid="info-icon"
      />
    )
  },
  info: {
    borderColor: colours.primary.dark,
    backgroundColor: colours.secondary.soft,
    Icon: (
      <InfoIcon
        boxSize="2rem"
        color={colours.primary.dark}
        mx={1}
        data-testid="info-icon"
      />
    )
  }
};

type Props = {
  notification: UserNotificationType;
  removeNotification: (code: string) => void;
};

export const Notification: React.FC<Props> = ({
  notification,
  removeNotification
}) => {
  const locale = useLocale();
  const { code, params, level } = notification;
  const { errorHeading, errorMessage, helpLink } = getNotificationData(
    locale,
    code,
    params
  );

  const { backgroundColor, borderColor, Icon } = notificationConfig[level];

  return (
    <Wrapper
      data-testid="notification-banner-item"
      backgroundColor={backgroundColor}
      borderLeft={`4px solid ${borderColor}`}
      alignItems="center"
    >
      <Box alignSelf="flex-start">{Icon}</Box>
      <Box justifyContent="left" letterSpacing="0.02em">
        {errorHeading && (
          <Text variant="acuminSemiBold" wordBreak="break-word" mt={0}>
            {errorHeading}
          </Text>
        )}
        {(errorMessage || helpLink) && (
          <Text
            mt={space[`${errorHeading ? "-5" : 0}`]}
            variant="acuminRegular"
            wordBreak="break-word"
            letterSpacing="0.02"
          >
            <Text
              as="span"
              data-testid="notification-message"
              dangerouslySetInnerHTML={createMarkup(errorMessage ?? "")}
            />
            {helpLink && (
              <Text as="span" data-testid="notification-help-link">
                {" "}
                {helpLink}
              </Text>
            )}
          </Text>
        )}
      </Box>
      <Spacer />
      <Box
        cursor="pointer"
        onClick={() => {
          removeNotification(code);
        }}
      >
        <CloseIcon
          color="black"
          boxSize="1.5rem"
          data-testid="cross-icon"
          aria-label="Close"
        />
      </Box>
    </Wrapper>
  );
};
